import {
  CAPABILITIES,
  resolveCapability,
  type CapabilityId,
  type EntitlementContext,
} from '@/services/entitlements';

/**
 * Single navigation registry.
 *
 * TRACE:TT-R145-01 — the shell exposes its sections without duplicated
 * destinations for the same feature. Every destination is declared exactly
 * once here with a `slot`, and the bottom bar and the More hub both read from
 * this list, so the same feature can never appear in both.
 *
 * TRACE:XS-SMEM-TT-R006-A2 — an entry the user is not entitled to is removed,
 * not greyed out.
 */

export type NavSlot = 'tab' | 'more';

export type NavGroup = 'Money' | 'Finance' | 'Business' | 'Account';

export type NavDestination = {
  readonly id: string;
  readonly label: string;
  readonly route: string;
  /** Short line shown in the More hub, omitted in the tab bar. */
  readonly description?: string;
  readonly group: NavGroup;
  readonly capability?: CapabilityId;
  /**
   * Which surface owns this destination. A destination is rendered in exactly
   * one place; `tab` items never also appear under More.
   */
  readonly slot: NavSlot;
  /** Renders as a handoff row that explains why it opens SME Web. */
  readonly handoff?: boolean;
};

export const DESTINATIONS: readonly NavDestination[] = [
  // --- primary tabs -----------------------------------------------------
  { id: 'home', label: 'Home', route: '/mobile/home', group: 'Money', slot: 'tab' },
  {
    id: 'accounts',
    label: 'Accounts',
    route: '/mobile/accounts',
    group: 'Money',
    capability: 'accountsView',
    slot: 'tab',
  },
  {
    id: 'payments',
    label: 'Payments',
    route: '/mobile/payments',
    group: 'Money',
    capability: 'paymentsCreate',
    slot: 'tab',
  },
  {
    id: 'approvals',
    label: 'Approvals',
    route: '/mobile/approvals',
    group: 'Money',
    capability: 'approvalsView',
    slot: 'tab',
  },
  {
    id: 'cards',
    label: 'Cards',
    route: '/mobile/cards',
    group: 'Money',
    capability: 'cardsView',
    slot: 'tab',
  },

  // --- More hub ---------------------------------------------------------
  {
    id: 'transactions',
    label: 'Transactions',
    route: '/mobile/transactions',
    description: 'Search and filter every movement',
    group: 'Money',
    capability: 'transactionsView',
    slot: 'more',
  },
  {
    id: 'beneficiaries',
    label: 'Beneficiaries',
    route: '/mobile/beneficiaries',
    description: 'People and businesses you pay',
    group: 'Money',
    capability: 'beneficiariesView',
    slot: 'more',
  },
  {
    id: 'fx',
    label: 'Exchange',
    route: '/mobile/fx',
    description: 'Convert between your currencies',
    group: 'Money',
    capability: 'fxView',
    slot: 'more',
  },

  {
    id: 'reports',
    label: 'Reports',
    route: '/mobile/reports',
    description: 'Summaries and statements',
    group: 'Finance',
    capability: 'reportsView',
    slot: 'more',
  },
  {
    id: 'insights',
    label: 'Insights',
    route: '/mobile/insights',
    description: 'What changed and what needs a decision',
    group: 'Finance',
    capability: 'aiSummary',
    slot: 'more',
  },
  {
    id: 'tax',
    label: 'Tax calendar',
    route: '/mobile/tax',
    description: 'Upcoming obligations',
    group: 'Finance',
    capability: 'taxView',
    slot: 'more',
  },
  {
    id: 'accounting',
    label: 'Connected accounting',
    route: '/mobile/connected-accounts',
    description: 'Bookkeeping links and sync status',
    group: 'Finance',
    capability: 'accountingView',
    slot: 'more',
  },
  {
    id: 'expenses',
    label: 'Expenses & receipts',
    route: '/mobile/expenses',
    // Expense management is a separate product; the entry point is controlled.
    description: 'Separate expense module',
    group: 'Finance',
    capability: 'expenseEntry',
    slot: 'more',
  },

  {
    id: 'invoices',
    label: 'Invoices',
    route: '/mobile/invoices',
    description: 'What you are owed',
    group: 'Finance',
    capability: 'reportsView',
    slot: 'more',
  },
  {
    id: 'company-profile',
    label: 'Company profile',
    route: '/mobile/company-profile',
    description: 'Identity and connected systems',
    group: 'Business',
    slot: 'more',
  },
  {
    id: 'team',
    label: 'Team',
    route: '/mobile/team',
    description: 'Who has access',
    group: 'Business',
    capability: 'teamView',
    slot: 'more',
  },
  {
    id: 'documents',
    label: 'Documents',
    route: '/mobile/documents',
    description: 'Company documents and verification',
    group: 'Business',
    capability: 'documentsView',
    slot: 'more',
  },
  {
    id: 'wallets-spaces',
    label: 'Wallets and spaces',
    route: '/mobile/wallets-spaces',
    description: 'Provider-gated',
    group: 'Business',
    capability: 'digitalAssetsView',
    slot: 'more',
  },

  {
    id: 'notifications',
    label: 'Notifications',
    route: '/mobile/notifications',
    description: 'Alerts and messages',
    group: 'Account',
    capability: 'notificationsView',
    slot: 'more',
  },
  {
    id: 'activity',
    label: 'Activity',
    route: '/mobile/activity',
    description: 'A record of what happened on this account',
    group: 'Account',
    slot: 'more',
  },
  {
    id: 'profile',
    label: 'Profile',
    route: '/mobile/profile',
    description: 'Your details and preferences',
    group: 'Account',
    slot: 'more',
  },
  {
    id: 'security',
    label: 'Security',
    route: '/mobile/security',
    description: 'Passkeys, MFA and trusted devices',
    group: 'Account',
    slot: 'more',
  },
  {
    id: 'continue-on-web',
    label: 'Continue on web',
    route: '/mobile/continue-on-web',
    description: 'Open the full desktop experience',
    group: 'Account',
    slot: 'more',
    handoff: true,
  },
];

/** Destinations the user is entitled to, for a given slot. */
export function visibleDestinations(
  ctx: EntitlementContext,
  slot: NavSlot,
): readonly NavDestination[] {
  return DESTINATIONS.filter((d) => d.slot === slot).filter((d) => {
    if (!d.capability) return true;
    const decision = resolveCapability(ctx, CAPABILITIES[d.capability]);
    // `explained` denials stay visible so the reason can be shown; only
    // `hidden` denials remove the entry point.
    return decision.allowed || decision.visibility === 'explained';
  });
}

export const MORE_GROUP_ORDER: readonly NavGroup[] = ['Money', 'Finance', 'Business', 'Account'];

/** Look up the owning destination for a path, for back-navigation defaults. */
export function destinationForPath(pathname: string): NavDestination | undefined {
  return DESTINATIONS.find((d) => pathname === d.route || pathname.startsWith(`${d.route}/`));
}

/**
 * Where Back should land for a given route.
 *
 * TRACE:TT-R128-03 — back from a More sub-section returns to More, not the
 * dashboard.
 * TRACE:TT-R145-02 — back consistently returns one step to the previous screen.
 */
export function defaultBackFor(pathname: string): { to: string; label: string } {
  const destination = destinationForPath(pathname);
  if (destination && destination.slot === 'more' && pathname === destination.route) {
    return { to: '/mobile/more', label: 'More' };
  }
  if (destination && pathname !== destination.route) {
    // A detail screen returns to its own list.
    return { to: destination.route, label: destination.label };
  }
  return { to: '/mobile/home', label: 'Home' };
}
